import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { formatUserPhoneNumber } from '@/components/settings-page/users/firmTable/tableBody';
import { themeVar } from '@/styles/theme';
import { Dispatch, SetStateAction, useState } from 'react';
import styled from 'styled-components';
import { TWO_FACTOR_AUTHENTICATION_STATUS_ENUM } from '..';

interface VerificationRequiredProps {
  isOpen: boolean;
  mobileNumber: { countryCode: string; number: string };
  sendVerificationCode: () => Promise<void>;
  handleStatusChange: (newStatus: TWO_FACTOR_AUTHENTICATION_STATUS_ENUM) => void;
  handleTwoFactorAuthenticationErrors: (error: any, setErrorMessage: Dispatch<SetStateAction<string | null>>) => void;
  onClose: () => void;
}

const VerificationRequired = ({
  isOpen,
  mobileNumber,
  sendVerificationCode,
  handleStatusChange,
  handleTwoFactorAuthenticationErrors,
  onClose
}: VerificationRequiredProps) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onSave = async () => {
    try {
      setErrorMessage(null);
      setLoading(true);

      await sendVerificationCode();

      handleStatusChange(TWO_FACTOR_AUTHENTICATION_STATUS_ENUM.VERIFY);
      onClose();
    } catch (error: any) {
      console.error(error);
      console.log(error.code);
      handleTwoFactorAuthenticationErrors(error, setErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Verification Required"
      confirmButton={{
        name: 'Continue',
        onClick: onSave,
        disabled: loading
      }}
      showCloseIcon={!loading}
      showCancelButton={!loading}
      isUseOutsideClick={!loading}
    >
      {loading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <ModalDescription color={themeVar('font.base')}>We will send you a text to verify this number:</ModalDescription>
          <PhoneNumber>{formatUserPhoneNumber(mobileNumber.countryCode, mobileNumber.number)}</PhoneNumber>
          <br />
          <ModalDescription color={themeVar('font.weak')}>Message and data rates may apply.</ModalDescription>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default VerificationRequired;

const ModalDescription = styled.div<{ color: string }>`
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${({ color }) => color};
`;

const PhoneNumber = styled.p`
  text-align: center;
  font-size: 19px;
  font-weight: 700;
  line-height: 28px;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 15px;
`;
